import React, {useState, useEffect, useRef, useCallback} from 'react';
import {Button, Card, Collapse, Dialog, DialogBody, Drawer, DrawerSize, EditableText, H3, H4, H5, H6, Icon, Popover, Tag, Tooltip} from '@blueprintjs/core';
import styled from 'styled-components';
import {sfVars, ErrorTag} from './shared';


const ItemCardStyled = styled(Card)`
    padding: 12px;
    margin: 0.25em 0;
    border-radius: var(--sf-border-radius);
    background: var(--sf-group-background) !important;

    &[class*="bp"][class*="-card"] {
        box-shadow: 0 0 0 1px rgba(17, 20, 24, 0.1),
                    0 1px 1px rgba(17, 20, 24, 0.1),
                    0 2px 6px rgba(17, 20, 24, 0.1);
    }

    &:hover ${() => RemoveButton} {
        visibility: visible;
    }
`;

const ItemHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: ${p => p.$collapsed ? '0' : '8px'};
    cursor: ${p => p.$collapsible ? 'pointer' : 'default'};
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    min-height: ${p => p.$collapsible ? '44px' : 'auto'};
`;

const CollapseIcon = styled(Icon)`
    color: #5c7080;
    cursor: pointer;
    padding: 10px;
    margin: -10px;
`;

const DragHandle = styled(Icon)`
    color: #5c7080;
    cursor: grab;
    &:active { cursor: grabbing; }
`;

const RemoveButton = styled(Button)`
    visibility: hidden;
`;


// Heading sizes: H3=22, H4=18, H5=16, H6=14 — description is 85% of that, floored at 12px
const HEADING_SIZES = [22, 18, 16, 14];
const descSize = (depth) => Math.max(HEADING_SIZES[Math.min(depth || 0, 3)] * 0.85, 12);

const ItemDescription = styled.div`
    color: #5f6b7c;
    font-size: ${p => descSize(p.$depth)}px;
    line-height: 1.3;
    margin-top: 2px;
    & [class*="bp"][class*="-editable-text-input"] {
        field-sizing: content;
        width: auto !important;
    }
`;


const PANEL_WIDTH = 'min(1080px, 90vw)';

const PopoverContent = styled.div`
    padding: 12px;
    max-height: 400px;
    overflow: auto;
`;

const PortalContent = styled.div`${sfVars}`;

const CompactBody = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--sf-horizontal-gap, 8px);
    flex: 1;
    min-width: 0;
`;

const PANEL_MODES = {popover: true, overlay: true, drawer: true};

const EditModeCard = ({editMode, readonly, collapsed, onToggleCollapse, errors, title, titleText, description, depth, panelWidth, actions, dragListeners, children}) => {
    const [panelOpen, setPanelOpen] = useState(false);
    const contentHidden = editMode === 'collapsible' ? collapsed : !!PANEL_MODES[editMode];
    const expandCard = contentHidden ? () => {
        if (editMode === 'collapsible') onToggleCollapse?.();
        else if (PANEL_MODES[editMode]) setPanelOpen(true);
    } : undefined;

    const leftSlot = (
        <div style={{flex: 1, minWidth: 0}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                {dragListeners && (
                    <Tooltip content={`Move ${titleText}`} placement="top" hoverOpenDelay={400}>
                        <DragHandle icon="drag-handle-vertical" {...dragListeners} />
                    </Tooltip>
                )}
                {editMode === 'collapsible' && (
                    <CollapseIcon
                        icon={collapsed ? 'caret-right' : 'caret-down'}
                        onClick={e => { e.stopPropagation(); onToggleCollapse(); }}
                    />
                )}
                {editMode === 'popover' && (
                    <Popover
                        interactionKind="hover"
                        placement="right-start"
                        content={
                            <PortalContent>
                                <PopoverContent style={{width: panelWidth || PANEL_WIDTH}}>
                                    <div style={{marginBottom: 12}}>
                                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>{title}</div>
                                        {description && <div style={{marginTop: 4}}>{description}</div>}
                                    </div>
                                    {children}
                                </PopoverContent>
                            </PortalContent>
                        }
                        hoverOpenDelay={200}
                        hoverCloseDelay={100}
                    >
                        <Icon icon={readonly ? 'eye-open' : 'edit'} style={{color: '#5c7080', cursor: 'pointer'}} />
                    </Popover>
                )}
                {(editMode === 'overlay' || editMode === 'drawer') && (
                    <Tooltip content={`${readonly ? 'View' : 'Edit'} ${titleText}`} placement="top" hoverOpenDelay={400}>
                        <Icon icon={readonly ? 'eye-open' : 'edit'} style={{color: '#5c7080', cursor: 'pointer'}} onClick={() => setPanelOpen(true)} />
                    </Tooltip>
                )}
                {title}
                {contentHidden && <ErrorTag errors={errors} onClick={expandCard} />}
            </div>
            {description}
        </div>
    );

    const renderBody = () => {
        if (editMode === 'collapsible') {
            return (
                <Collapse isOpen={!collapsed}>
                    {children}
                </Collapse>
            );
        }
        if (editMode === 'compact' || editMode === 'popover') return null;
        if (editMode === 'overlay') {
            return (
                <Dialog isOpen={panelOpen} onClose={() => setPanelOpen(false)} title={title || titleText} style={{width: panelWidth || PANEL_WIDTH}}>
                    <DialogBody>
                        <PortalContent>
                            {description && <div style={{marginBottom: 12}}>{description}</div>}
                            {children}
                        </PortalContent>
                    </DialogBody>
                </Dialog>
            );
        }
        if (editMode === 'drawer') {
            return (
                <Drawer isOpen={panelOpen} onClose={() => setPanelOpen(false)} size={panelWidth || PANEL_WIDTH} title={title || titleText}>
                    <PortalContent style={{padding: 20}}>
                        {description && <div style={{marginBottom: 12}}>{description}</div>}
                        {children}
                    </PortalContent>
                </Drawer>
            );
        }
        return children;
    };

    if (editMode === 'compact') {
        return (
            <ItemCardStyled>
                <CompactBody>
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        {dragListeners && (
                            <Tooltip content={`Move ${titleText}`} placement="top" hoverOpenDelay={400}>
                                <DragHandle icon="drag-handle-vertical" {...dragListeners} />
                            </Tooltip>
                        )}
                        {title}
                        {description}
                    </div>
                    {children}
                    {actions}
                </CompactBody>
            </ItemCardStyled>
        );
    }

    return (
        <ItemCardStyled>
            <ItemHeader
                $collapsed={contentHidden}
                $collapsible={editMode === 'collapsible'}
            >
                {leftSlot}
                {actions}
            </ItemHeader>
            {renderBody()}
        </ItemCardStyled>
    );
};


// --- Shared editable title/description components ---

const HEADINGS = [H3, H4, H5, H6];
const headingFor = (depth) => HEADINGS[Math.min(depth || 0, HEADINGS.length - 1)];

const ItemLabel = styled(H5)`
    margin: 0;
    color: var(--sf-group-label-color);
`;

const TitleLabel = styled(H5)`
    margin: 0;
    color: var(--sf-group-label-color);
    & [class*="bp"][class*="-editable-text-input"] {
        field-sizing: content;
        width: auto !important;
    }
`;

const TitleError = styled.div`
    color: #cd4246;
    font-size: 12px;
    margin-top: 2px;
`;

const coerceTitle = (val, type) => {
    if (type === 'number' || type === 'integer') {
        const n = Number(val);
        return isNaN(n) ? val : n;
    }
    return val;
};

const DEBOUNCE_MS = 500;

const EditableTitle = ({value, disabled, error, type, depth, placeholder, onChange}) => {
    const [text, setText] = useState(String(value ?? ''));
    const timerRef = useRef(null);
    useEffect(() => setText(String(value ?? '')), [value]);
    useEffect(() => () => clearTimeout(timerRef.current), []);
    const handleChange = useCallback((val) => {
        setText(val);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onChange(coerceTitle(val, type)), DEBOUNCE_MS);
    }, [onChange, type]);
    const handleConfirm = useCallback((val) => {
        clearTimeout(timerRef.current);
        onChange(coerceTitle(val, type));
    }, [onChange, type]);
    return (
        <div>
            <TitleLabel as={headingFor(depth)}>
                <EditableText
                    value={text}
                    onChange={handleChange}
                    onConfirm={handleConfirm}
                    disabled={disabled}
                    placeholder={placeholder}
                    selectAllOnFocus
                    intent={error ? 'danger' : 'none'}
                />
            </TitleLabel>
            {error && <TitleError>{error.message}</TitleError>}
        </div>
    );
};

const DescriptionError = styled.div`
    color: #cd4246;
    font-size: 11px;
    margin-top: 1px;
`;

const EditableDescription = ({value, disabled, error, type, depth, placeholder, onChange}) => {
    const [text, setText] = useState(String(value ?? ''));
    const timerRef = useRef(null);
    useEffect(() => setText(String(value ?? '')), [value]);
    useEffect(() => () => clearTimeout(timerRef.current), []);
    const handleChange = useCallback((val) => {
        setText(val);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onChange(coerceTitle(val, type)), DEBOUNCE_MS);
    }, [onChange, type]);
    const handleConfirm = useCallback((val) => {
        clearTimeout(timerRef.current);
        onChange(coerceTitle(val, type));
    }, [onChange, type]);
    return (
        <ItemDescription $depth={depth}>
            <EditableText
                value={text}
                onChange={handleChange}
                onConfirm={handleConfirm}
                disabled={disabled}
                placeholder={placeholder}
                selectAllOnFocus
                intent={error ? 'danger' : 'none'}
                maxLines={1}
            />
            {error && <DescriptionError>{error.message}</DescriptionError>}
        </ItemDescription>
    );
};


export {EditModeCard, ItemCardStyled, ItemHeader, ItemDescription, CollapseIcon, DragHandle, RemoveButton, headingFor, ItemLabel, TitleLabel, coerceTitle, EditableTitle, EditableDescription};
